import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { skipWaiting } from "@/serviceWorkerRegistration";

/**
 * Bottom-of-screen banner shown when a newer service worker has installed.
 *
 * The banner is intentionally non-blocking so the user can finish whatever
 * they're doing and reload on their own terms. We do NOT auto-reload — that
 * would lose the global-filters state that lives in URL params for each tab.
 *
 * Listens to two custom events dispatched by `serviceWorkerRegistration.js`:
 *   - `dc:sw-updated`   → a new SW finished install (waiting state).
 *   - `dc:sw-activated` → a new SW just took control of this window.
 */
export default function UpdateBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onUpdated = () => setVisible(true);
    const onActivated = () => setVisible(true);
    window.addEventListener("dc:sw-updated", onUpdated);
    window.addEventListener("dc:sw-activated", onActivated);
    return () => {
      window.removeEventListener("dc:sw-updated", onUpdated);
      window.removeEventListener("dc:sw-activated", onActivated);
    };
  }, []);

  if (!visible) return null;

  const handleReload = () => {
    setReloading(true);
    // Tell the waiting SW (if any) to take over, then hard-reload bypassing cache.
    try {
      skipWaiting();
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      // `location.reload(true)` is deprecated; this combo is the modern equivalent.
      window.location.reload();
    }, 200);
  };

  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 bottom-3 z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-lg border border-primary/40 bg-card/95 px-4 py-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="leading-tight">
          <div className="text-sm font-medium">{t("update.title")}</div>
          <div className="text-[11px] text-muted-foreground">
            {t("update.subtitle")}
          </div>
        </div>
      </div>
      <Button
        size="sm"
        onClick={handleReload}
        disabled={reloading}
        data-testid="update-banner-reload"
      >
        <RefreshCw
          className={"mr-1.5 h-3.5 w-3.5 " + (reloading ? "animate-spin" : "")}
        />
        {t("update.reload")}
      </Button>
    </div>
  );
}
